/**
 * The auth screen: sign in, or create the character (username, password,
 * class, look). Pre-game only — it posts to the dev server's pre-game routes
 * and hands the lobby a token; nothing here ever touches the game path or
 * the sim. The class is stored, not simulated (classes land later).
 *
 * The page IS the town: the square behind the form is drawn by `authScene.ts`
 * through the same town art the hub renders (door, braziers, pools, floor
 * wear), so signing in and landing are the same place. It is a still — no
 * socket, no roster, no presence. The town's people arrive with the token
 * that authenticates the hub socket; an anonymous pre-auth presence feed
 * would leak who is online to every visitor.
 *
 * The creator is deliberately small. Body customization is four garment
 * slots — top, feet, pants, hair — plus the skin colour. Everything else
 * (torso/head/weapon/shield) is EQUIPMENT, not a look: it comes from the
 * loadout, never from creation. Each garment gets one calm row:
 *
 *   Top      < tunic >   [ Crimson + ]
 *
 * the arrows cycle the slot's options — the unequipped state (labelled per
 * slot: a bare chest, no shoes, bare legs, or bald) plus the garment
 * variants — and the bracketed button shows the current colour (the
 * friendly-named default when nothing is picked, never the word "canon") and
 * opens that region's picker (the shared palette store). One picker open at
 * a time. The `legs` region paints both the pants and the boots, so those
 * two rows share one colour. The skin row has no garment to cycle — it is
 * colour only.
 *
 * The hero you are building stands IN the square, in the crowd-band slot a
 * real arrival gets, at the size it will actually be seen — the scene is the
 * preview, not a card next to it. It is lazy: the sign-in page loads the
 * atlas for the square but never the hero's part frames. One atlas fetch
 * feeds both. If the art fails, the square draws nothing and the character
 * can still be created (canon look) — a missing picture never blocks sign-in.
 */
import { CLASSES, PALETTE, type HeroClass, type RegionColours, type Swatch } from "@pd/shared";
import { AuthScene, type SceneHero } from "./authScene.js";
import type { Atlas } from "./atlas.js";
import {
  ensureReady,
  loadAtlas,
  previewFromAtlas,
  refreshLut,
  type HeroPreview,
} from "./heroPreview.js";
import { WorldArt } from "./worldArt.js";
import { apiCreateAccount, apiLogin, setAuthToken } from "./auth.js";

export interface AuthScreenCallbacks {
  /** A valid session token is stored — show the lobby. */
  onAuthed(username: string): void;
}

/** The creator's rows (display order): the four BODY slots + the skin
 *  colour. The `feet` slot contains footwear — "none" there
 *  simply means no shoes. torso/head/weapon/shield are equipment — the
 *  loadout owns them, not the character. Skin has no garment to cycle, so
 *  its row is colour-only (slot null). */
const LOOK_SLOTS: { slot: string | null; label: string; region: string }[] = [
  { slot: "top", label: "Top", region: "top" },
  { slot: "feet", label: "Feet", region: "legs" },
  { slot: "pants", label: "Pants", region: "legs" },
  { slot: "hair", label: "Hair", region: "hair" },
  { slot: null, label: "Skin", region: "skin" },
];

/** The unequipped state of each slot, named for the user: a bare chest,
 *  no shoes ("none" — literally), bare legs (no pants), or bald. */
const ZERO_LABEL: Record<string, string> = {
  top: "bare",
  feet: "none",
  pants: "bare",
  hair: "bald",
};

/** "Canon" is pipeline jargon, not a colour. The chip instead wears the
 *  real canon ramp (art data — PALETTE.canon.classes) of the class the
 *  region's default garment is drawn in, under a friendly name for that
 *  colour. */
const CANON_DISPLAY: Record<string, { cls: string; name: string }> = {
  top: { cls: "tunic", name: "Slate" },
  legs: { cls: "leather", name: "Tan" },
  hair: { cls: "hair", name: "Auburn" },
  skin: { cls: "skin", name: "Fair" },
};

/** The canon chip/button display for a region: friendly name + the strip
 *  colours in display order (deep, shadow, base, light — TONE_FIELDS is
 *  base, shadow, light, deep). */
function canonDisplay(region: string): { name: string; strip: (string | undefined)[] | null } {
  const disp = CANON_DISPLAY[region];
  if (!disp) return { name: "default", strip: null };
  const ramp = PALETTE.canon.classes[disp.cls];
  if (!ramp) return { name: disp.name, strip: null };
  return { name: disp.name, strip: [ramp[3], ramp[1], ramp[0], ramp[2]] };
}

/** Picker captions — `legs` paints the pants and the boots together. */
const REGION_NOTES: Record<string, string> = {
  top: "top colour",
  legs: "legs colour (pants and boots)",
  hair: "hair colour",
  skin: "skin colour",
};

interface LookRow {
  /** null = the skin row (colour only, no garment to cycle). */
  slot: string | null;
  region: string;
  /** The slot's garment variants, in catalogue order (index 0 of the cycle
   *  is the unequipped state, labelled ZERO_LABEL[slot]). */
  variants: string[];
  zeroLabel: string;
  nameEl: HTMLSpanElement | null;
  colorBtn: HTMLButtonElement;
  colorStrips: HTMLSpanElement;
  colorName: HTMLSpanElement;
  pickEl: HTMLDivElement;
}

export class AuthScreen {
  private root: HTMLElement;
  private callbacks: AuthScreenCallbacks;

  private mode: "login" | "create" = "login";
  private look: RegionColours = {};
  private outfit: Record<string, string | null> = {};
  private pv: HeroPreview | null = null;
  private pvStarted = false;
  private busy = false;

  private sceneCanvas!: HTMLCanvasElement;
  private scene: AuthScene | null = null;
  private art: WorldArt | null = null;
  private atlas: Atlas | null = null;
  /** The atlas fetch/parse failed. The creator must still work — colour is
   *  store data and the outfit defaults server-side, so a missing picture
   *  may never block account creation. */
  private atlasFailed = false;
  private panel!: HTMLDivElement;
  private createCols!: HTMLDivElement;
  private loginPane!: HTMLDivElement;
  private createPane!: HTMLDivElement;
  private lookPane!: HTMLDivElement;
  private toggleEl!: HTMLButtonElement;
  private errorEl!: HTMLDivElement;
  private outfitWrap!: HTMLDivElement;
  private previewNote!: HTMLDivElement;
  private classSel!: HTMLSelectElement;
  private createBtn!: HTMLButtonElement;
  private rows: LookRow[] = [];
  private openPick: string | null = null;
  private raf = 0;
  private onResize = (): void => this.handleResize();

  constructor(root: HTMLElement, callbacks: AuthScreenCallbacks) {
    this.root = root;
    this.callbacks = callbacks;
    this.build();
    void this.startScene();
    window.addEventListener("resize", this.onResize);
  }

  /** Called on phase exit: stop the anim loop (the root is being cleared). */
  destroy(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    window.removeEventListener("resize", this.onResize);
  }

  /** The hero loop runs only while a hero is on the square: the square
   *  itself is a cached layer, so a static page costs nothing per frame. */
  private loop(t: number): void {
    if (this.raf === 0) return;
    this.drawScene(t);
    if (this.pv?.ready) this.raf = requestAnimationFrame((t2) => this.loop(t2));
    else this.raf = 0;
  }

  private drawScene(tMs: number): void {
    if (!this.scene) return;
    const hero: SceneHero | null =
      this.pv && this.pv.ready ? { assets: this.pv.assets, outfit: this.pv.outfit, lut: this.pv.lut } : null;
    this.scene.draw(tMs, hero);
  }

  /** Immediate repaint (headless/background tabs throttle rAF to a crawl —
   *  a click must show its result without waiting for the next frame). */
  private redrawNow(tMs = performance.now()): void {
    this.drawScene(tMs);
  }

  private handleResize(): void {
    this.scene?.invalidate();
    this.redrawNow();
  }

  /** The square loads the atlas and stands up the scene. Null art = the
   *  page is a plain form (RENDERING.md: missing art draws nothing). */
  private async startScene(): Promise<void> {
    const atlas = await loadAtlas();
    if (!atlas) {
      this.atlasFailed = true;
      if (this.mode === "create") this.startPreviewIfIdle();
      return;
    }
    this.atlas = atlas;
    this.art = new WorldArt(atlas);
    this.scene = new AuthScene(this.sceneCanvas, this.art);
    this.redrawNow();
    // The creator may have been opened while the atlas was still in flight.
    if (this.mode === "create") this.startPreviewIfIdle();
  }

  /** The hero is lazy: the sign-in page never loads (or shows) one. First
   *  entry into the creator starts it; the outfit rows are built from the
   *  atlas once it lands (slots are pipeline data, not client code). */
  private startPreviewIfIdle(): void {
    if (this.pvStarted) return;
    if (!this.atlas) {
      // No art (still loading, or failed). Loading: the scene's own start
      // re-enters here when the atlas lands. Failed: go straight to the
      // no-preview path so the form is never gated on a picture.
      this.previewNote.textContent = this.atlasFailed
        ? "No preview. You can still choose colours; the default outfit will be used."
        : "loading your hero…";
      if (!this.atlasFailed) return;
      this.pvStarted = true;
      this.buildLookRows();
      this.createBtn.disabled = false;
      return;
    }
    this.pvStarted = true;
    this.previewNote.textContent = "loading your hero…";
    const pv = this.atlas ? previewFromAtlas(this.atlas) : null;
    if (!pv) {
      this.pvStarted = false;
      this.previewNote.textContent =
        "No preview. You can still choose colours; the default outfit will be used.";
      const retry = document.createElement("button");
      retry.className = "preview-retry";
      retry.textContent = "Retry preview";
      retry.addEventListener("click", () => this.startPreviewIfIdle());
      this.previewNote.appendChild(retry);
      this.buildLookRows();
      this.createBtn.disabled = false;
      return;
    }
    this.pv = pv;
    pv.look = { ...this.look };
    refreshLut(pv);
    for (const [slot, v] of Object.entries(pv.outfit)) this.outfit[slot] = v;
    this.previewNote.textContent = "";
    void ensureReady(pv).then(() => {
      pv.ready = true;
      this.redrawNow();
      if (this.raf === 0) this.raf = requestAnimationFrame((t) => this.loop(t));
    });
    this.buildLookRows();
    this.createBtn.disabled = false;
  }

  private build(): void {
    this.root.textContent = "";
    const el = document.createElement("div");
    el.id = "auth";

    // The town square, full-bleed, behind the form. Decoration over the same
    // art the town shows — it carries no state and says nothing to a
    // screen reader.
    this.sceneCanvas = document.createElement("canvas");
    this.sceneCanvas.className = "auth-scene";
    this.sceneCanvas.setAttribute("aria-hidden", "true");
    el.appendChild(this.sceneCanvas);

    // The bar: the wordmark, and the one thing a visitor can do WITHOUT an
    // account. It is a control, not a caption — same place, same shape,
    // before and after signing in (the hub's bar carries it too).
    const bar = document.createElement("div");
    bar.className = "auth-bar";
    const h1 = document.createElement("h1");
    h1.textContent = "PERFECT DUNGEON";
    const boards = document.createElement("a");
    boards.href = "/leaderboard";
    boards.dataset.records = "";
    boards.className = "boards";
    boards.textContent = "The boards";
    bar.append(h1, boards);
    el.appendChild(bar);

    // The panel: whichever form is showing, plus the outfit column and the
    // shared toggle/error.
    this.panel = document.createElement("div");
    this.panel.className = "auth-panel";
    this.loginPane = this.buildLoginForm();
    this.createPane = this.buildCreateForm();

    this.createCols = document.createElement("div");
    this.createCols.className = "auth-create-cols";
    this.lookPane = document.createElement("div");
    this.lookPane.className = "auth-pane look-pane";
    const sectionLb = document.createElement("div");
    sectionLb.className = "section-lb";
    sectionLb.textContent = "Outfit";
    this.outfitWrap = document.createElement("div");
    this.outfitWrap.className = "look-wrap";
    this.previewNote = document.createElement("div");
    this.previewNote.className = "hint preview-note";
    this.lookPane.append(sectionLb, this.outfitWrap, this.previewNote);
    this.createCols.append(this.createPane, this.lookPane);

    this.toggleEl = document.createElement("button");
    this.toggleEl.className = "hint toggle";
    this.toggleEl.addEventListener("click", () => {
      if (this.busy) return;
      this.mode = this.mode === "login" ? "create" : "login";
      this.renderMode();
    });
    this.errorEl = document.createElement("div");
    this.errorEl.className = "error";
    this.errorEl.setAttribute("aria-live", "polite");

    this.panel.append(this.loginPane, this.createCols, this.toggleEl, this.errorEl);
    el.appendChild(this.panel);
    this.root.appendChild(el);
    this.renderMode();
  }

  private field(label: string, input: HTMLInputElement | HTMLSelectElement): HTMLLabelElement {
    const wrap = document.createElement("label");
    wrap.className = "field";
    const lb = document.createElement("span");
    lb.className = "hint";
    lb.textContent = label;
    wrap.append(lb, input);
    return wrap;
  }

  private buildLoginForm(): HTMLDivElement {
    const pane = document.createElement("div");
    pane.className = "auth-pane";
    const title = document.createElement("h3");
    title.textContent = "Sign in";
    pane.appendChild(title);
    const user = document.createElement("input");
    user.placeholder = "username";
    user.maxLength = 20;
    user.autocomplete = "username";
    const pass = document.createElement("input");
    pass.type = "password";
    pass.placeholder = "password";
    pass.autocomplete = "current-password";
    pane.append(this.field("Username", user), this.field("Password", pass));
    const btn = document.createElement("button");
    btn.className = "full";
    btn.textContent = "Sign in";
    btn.addEventListener("click", async () => {
      if (this.busy) return;
      this.busy = true;
      btn.disabled = true;
      btn.textContent = "Signing in…";
      this.error("");
      const r = await apiLogin(user.value.trim(), pass.value);
      this.busy = false;
      btn.disabled = false;
      btn.textContent = "Sign in";
      if (!r.ok) {
        this.error(r.error ?? "login failed");
        return;
      }
      setAuthToken(r.token ?? null, r.username);
      this.callbacks.onAuthed(r.username ?? user.value.trim());
    });
    for (const input of [user, pass]) input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") btn.click();
    });
    pane.appendChild(btn);
    return pane;
  }

  private buildCreateForm(): HTMLDivElement {
    const pane = document.createElement("div");
    pane.className = "auth-pane";
    const title = document.createElement("h3");
    title.textContent = "Create a character";
    pane.appendChild(title);
    const user = document.createElement("input");
    user.placeholder = "username";
    user.maxLength = 20;
    user.autocomplete = "username";
    const pass = document.createElement("input");
    pass.type = "password";
    pass.placeholder = "password (min 4)";
    pass.autocomplete = "new-password";
    this.classSel = document.createElement("select");
    for (const c of CLASSES) {
      const opt = document.createElement("option");
      opt.value = c;
      opt.textContent = c;
      this.classSel.appendChild(opt);
    }
    pane.append(
      this.field("Username", user),
      this.field("Password", pass),
      this.field("Class", this.classSel),
    );

    this.createBtn = document.createElement("button");
    const btn = this.createBtn;
    btn.className = "full";
    btn.textContent = "Create character";
    btn.disabled = true;
    btn.addEventListener("click", async () => {
      if (this.busy) return;
      this.busy = true;
      btn.disabled = true;
      btn.textContent = "Creating…";
      this.error("");
      const cls = this.classSel.value as HeroClass;
      const r = await apiCreateAccount({
        username: user.value.trim(),
        password: pass.value,
        class: cls,
        appearance: { outfit: { ...this.outfit }, colours: { ...this.look } },
      });
      if (!r.ok) {
        this.busy = false;
        btn.disabled = false;
        btn.textContent = "Create character";
        this.error(r.error ?? "create failed");
        return;
      }
      const login = await apiLogin(user.value.trim(), pass.value);
      this.busy = false;
      btn.disabled = false;
      btn.textContent = "Create character";
      if (!login.ok) {
        this.mode = "login";
        this.renderMode();
        const loginInputs = this.loginPane.querySelectorAll("input");
        loginInputs[0]!.value = user.value;
        loginInputs[1]!.value = pass.value;
        this.error(`${r.username} is ready. Sign in to enter the hub. (${login.error ?? "Sign-in failed."})`);
        return;
      }
      setAuthToken(login.token ?? null, login.username);
      this.callbacks.onAuthed(login.username ?? user.value.trim());
    });
    for (const input of [user, pass, this.classSel]) input.addEventListener("keydown", (e) => {
      if (e instanceof KeyboardEvent && e.key === "Enter") btn.click();
    });
    pane.appendChild(btn);
    return pane;
  }

  /** The five look rows: `Label  < variant >  [ Colour + ]` (skin has no
   *  variant — `Label  [ Colour + ]`), each with an inline colour picker
   *  that opens under it (one open at a time). Built once the atlas lands —
   *  the variants are pipeline data. */
  private buildLookRows(): void {
    const hero = this.pv?.assets.hero;
    this.outfitWrap.textContent = "";
    this.rows = [];
    this.openPick = null;
    for (const { slot, label, region } of LOOK_SLOTS) {
      const variants = slot ? (hero?.outfit?.slots[slot] ?? []) : [];
      if (slot && variants.length === 0 && hero) continue;
      const zeroLabel = slot ? (ZERO_LABEL[slot] ?? "none") : "";

      const row = document.createElement("div");
      row.className = "look";
      const line = document.createElement("div");
      line.className = "look-line";
      const lb = document.createElement("span");
      lb.className = "look-lb";
      lb.textContent = label;
      line.appendChild(lb);

      let nameEl: HTMLSpanElement | null = null;
      let prev: HTMLButtonElement | null = null;
      let next: HTMLButtonElement | null = null;
      if (slot && variants.length > 0) {
        const cycler = document.createElement("div");
        cycler.className = "cycler";
        prev = document.createElement("button");
        prev.className = "cyc";
        prev.setAttribute("aria-label", `${label}: previous`);
        prev.textContent = "‹";
        nameEl = document.createElement("span");
        nameEl.className = "cyc-name";
        next = document.createElement("button");
        next.className = "cyc";
        next.setAttribute("aria-label", `${label}: next`);
        next.textContent = "›";
        cycler.append(prev, nameEl, next);
        line.appendChild(cycler);
      }

      const colorBtn = document.createElement("button");
      colorBtn.className = "colpick";
      colorBtn.setAttribute("aria-label", `${label}: pick colour`);
      const colorStrips = document.createElement("span");
      colorStrips.className = "col-strips";
      for (let i = 0; i < 4; i++) colorStrips.appendChild(document.createElement("i"));
      const colorName = document.createElement("span");
      colorName.className = "col-name";
      const plus = document.createElement("span");
      plus.className = "plus";
      plus.textContent = "+";
      colorBtn.append(colorStrips, colorName, plus);

      line.appendChild(colorBtn);
      row.appendChild(line);

      // The inline picker (hidden until its row's + is clicked).
      const pickEl = document.createElement("div");
      pickEl.className = "look-pick";
      pickEl.hidden = true;
      const pickLb = document.createElement("span");
      pickLb.className = "hint pick-lb";
      pickLb.textContent = REGION_NOTES[region] ?? `${region} colour`;
      const grid = document.createElement("div");
      grid.className = "pick-grid";
      // The canon choice, dressed as a real swatch: the region's default
      // class ramp (art data) under a friendly name — never the word "canon".
      const canon = document.createElement("button");
      canon.className = "pick pick-canon";
      const cd = canonDisplay(region);
      const canonStrips = document.createElement("span");
      canonStrips.className = "sw";
      for (const hex of cd.strip ?? []) {
        if (!hex) continue;
        const i = document.createElement("i");
        i.style.background = hex;
        canonStrips.appendChild(i);
      }
      const canonNm = document.createElement("span");
      canonNm.className = "nm";
      canonNm.textContent = cd.name;
      canon.append(canonStrips, canonNm);
      grid.appendChild(canon);
      const group = PALETTE.groups.find((g) => g.key === region);
      for (const sw of group?.swatches ?? []) grid.appendChild(this.makePick(sw));
      pickEl.append(pickLb, grid);
      row.appendChild(pickEl);
      this.outfitWrap.appendChild(row);

      if (slot && prev && next && nameEl) {
        const cycle = (dir: number): void => {
          const cur = this.outfit[slot] ?? null;
          const n = variants.length + 1; // unequipped state + the catalogue
          const i = cur ? variants.indexOf(cur) + 1 : 0;
          const ni = (i + dir + n) % n;
          const v = ni === 0 ? null : variants[ni - 1]!;
          this.outfit[slot] = v;
          nameEl.textContent = v ?? zeroLabel;
          if (this.pv) {
            this.pv.outfit = { ...this.outfit };
            void ensureReady(this.pv).then(() => this.redrawNow());
          }
        };
        prev.addEventListener("click", () => cycle(-1));
        next.addEventListener("click", () => cycle(1));
      }

      colorBtn.addEventListener("click", () => {
        const pickKey = slot ?? "skin";
        this.openPick = this.openPick === pickKey ? null : pickKey;
        this.syncPicks();
      });
      const setSwatch = (sw: Swatch | null): void => {
        // Click the current swatch again (or "canon") = back to canon.
        this.look[region] = sw && sw.id !== this.look[region] ? sw.id : 0;
        if (this.look[region] === 0) delete this.look[region];
        this.syncColours();
        if (this.pv) {
          this.pv.look = { ...this.look };
          refreshLut(this.pv);
          void ensureReady(this.pv).then(() => this.redrawNow());
        }
      };
      canon.addEventListener("click", () => setSwatch(null));
      for (const b of Array.from(grid.querySelectorAll<HTMLButtonElement>(".pick:not(.pick-canon)"))) {
        b.addEventListener("click", () => {
          const sw = group?.swatches.find((s) => s.id === Number(b.dataset.id));
          if (sw) setSwatch(sw);
        });
      }

      const lookRow: LookRow = { slot, region, variants, zeroLabel, nameEl, colorBtn, colorStrips, colorName, pickEl };
      this.rows.push(lookRow);
    }
    this.syncColours();
  }

  private makePick(sw: Swatch): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = "pick";
    b.dataset.id = String(sw.id);
    b.title = sw.name;
    const strips = document.createElement("span");
    strips.className = "sw";
    for (const hex of [sw.deep, sw.shadow, sw.base, sw.light]) {
      const i = document.createElement("i");
      i.style.background = hex;
      strips.appendChild(i);
    }
    const nm = document.createElement("span");
    nm.className = "nm";
    nm.textContent = sw.name;
    b.append(strips, nm);
    return b;
  }

  private swatchFor(region: string, id: number | undefined): Swatch | null {
    const group = PALETTE.groups.find((g) => g.key === region);
    return group?.swatches.find((s) => s.id === id) ?? null;
  }

  /** One row's variant name + colour button + picker selection state. */
  private syncRow(r: LookRow): void {
    if (r.nameEl && r.slot) r.nameEl.textContent = this.outfit[r.slot] ?? r.zeroLabel;
    const sw = this.swatchFor(r.region, this.look[r.region]);
    // The button always shows a real colour: the picked swatch's ramp, or
    // the region's canon ramp (a friendly-named default — see canonDisplay).
    const hexes = sw ? [sw.deep, sw.shadow, sw.base, sw.light] : (canonDisplay(r.region).strip ?? []);
    r.colorStrips.hidden = hexes.length === 0;
    r.colorStrips.querySelectorAll("i").forEach((el, k) => {
      el.style.background = hexes[k] ?? "";
    });
    r.colorName.textContent = sw ? sw.name : canonDisplay(r.region).name;
    r.pickEl.querySelector(".pick-canon")?.classList.toggle("selected", sw === null);
    for (const b of Array.from(r.pickEl.querySelectorAll<HTMLButtonElement>(".pick:not(.pick-canon)"))) {
      b.classList.toggle("selected", sw !== null && sw.id === Number(b.dataset.id));
    }
  }

  private syncColours(): void {
    for (const r of this.rows) this.syncRow(r);
  }

  /** Exactly one picker open at a time (the row whose + was last clicked). */
  private syncPicks(): void {
    for (const r of this.rows) r.pickEl.hidden = this.openPick !== (r.slot ?? "skin");
  }

  private renderMode(): void {
    document.getElementById("auth")?.classList.toggle("is-create", this.mode === "create");
    this.loginPane.hidden = this.mode !== "login";
    this.createCols.hidden = this.mode !== "create";
    this.toggleEl.textContent = this.mode === "login" ? "no account? create a character" : "have an account? sign in";
    if (this.createPane.hidden) this.error("");
    if (this.mode === "create") this.startPreviewIfIdle();
    this.redrawNow();
  }

  private error(msg: string): void {
    this.errorEl.textContent = msg;
  }

  /** A word from the host on the form that is ALREADY showing ("session
   *  expired"). The host must never re-build this screen to say something —
   *  a rebuild recreates the inputs, so anything typed so far disappears.
   *  That is how a dropped session looked like a render loop: the client
   *  retried with the dead credential, and every bounce re-mounted the form
   *  under the player's fingers (net.ts's authDead is the other half). */
  showNotice(msg: string): void {
    this.error(msg);
  }
}
